/**
 * AfterCare — Database layer using dual RuVector engines:
 *
 * 1. @ruvector/rvf-wasm (46KB) — Loads drugs.rvf for HNSW vector search
 * 2. @ruvector/rvlite (865KB)  — Native vector store API for drug data
 *
 * RvLite is a VECTOR DATABASE, not SQLite. Its SQL parser is very limited
 * (no PRIMARY KEY, IF NOT EXISTS, COUNT, CREATE INDEX, etc.).
 * We use its native API: insert_with_id(), get(), search(), export_json().
 *
 * Structured data (patient meds, dose logs, audit) uses localStorage
 * with JSON serialization — simple, reliable, fully offline.
 *
 * Zero network calls — everything runs on-device.
 */

import initRvLite, { RvLite, RvLiteConfig } from '@ruvector/rvlite';
import { initRvfStore, queryRvf, isRvfReady } from './rvf-store';
import { seedDemoEmr } from './emr-store';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VECTOR_DIMENSIONS = 384;
const DISTANCE_METRIC = 'cosine';

const LS_KEY_PATIENT_MEDS = 'aftercare:patient_medications';
const LS_KEY_DOSE_LOG = 'aftercare:dose_log';
const LS_KEY_AUDIT_LOG = 'aftercare:audit_log';
const LS_KEY_DRUG_INTERACTIONS = 'aftercare:drug_interactions';
const LS_KEY_SEEDED = 'aftercare:data_seeded';
const LS_KEY_PATIENT_NAME = 'aftercare:patient_name';
const LS_KEY_DRUG_CACHE = 'aftercare:drug_cache';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function esc(val: string): string {
  return val.replace(/'/g, "''");
}

// ---------------------------------------------------------------------------
// Singleton state
// ---------------------------------------------------------------------------

let dbInstance: RvLite | null = null;
let initPromise: Promise<RvLite | null> | null = null;

// ---------------------------------------------------------------------------
// localStorage-backed structured data stores
// ---------------------------------------------------------------------------

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return fallback;
}

function lsSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.warn('[AfterCare] localStorage write failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Data seeding — uses RvLite native API + localStorage
// ---------------------------------------------------------------------------

/**
 * Seed drug data into BOTH localStorage (primary, always reliable) and
 * RvLite WASM (optional, for vector search). The app works fully from
 * localStorage even if WASM never initializes.
 */
async function seedDrugData(db: RvLite | null): Promise<void> {
  // Check if already seeded in localStorage.
  const existingCache = localStorage.getItem(LS_KEY_DRUG_CACHE);
  if (existingCache && localStorage.getItem(LS_KEY_SEEDED) === 'true') {
    // If we have a working db, also check it has data.
    if (!db || db.len() > 0) {
      const cacheSize = JSON.parse(existingCache).length;
      console.log(`[AfterCare] Drug data already seeded (${cacheSize} drugs in localStorage)`);
      return;
    }
  }

  console.log('[AfterCare] Seeding drug database from drug-metadata.json...');

  try {
    const metaResp = await fetch('/drug-metadata.json');
    if (!metaResp.ok) throw new Error(`drug-metadata.json: ${metaResp.status}`);
    const drugs: Record<string, unknown>[] = await metaResp.json();

    // PRIMARY: Store all drug metadata in localStorage (always works).
    const drugCache: Record<string, Record<string, unknown>> = {};
    for (const drug of drugs) {
      const id = String(drug.id ?? '');
      if (!id) continue;
      drugCache[id] = {
        generic_name: String(drug.generic_name ?? ''),
        brand_names: drug.brand_names ?? [],
        description: String(drug.description ?? '').slice(0, 4000),
        dosage_forms: drug.dosage_forms ?? [],
        strengths: drug.strengths ?? [],
        dosage_instructions: String(drug.dosage_instructions ?? ''),
        side_effects: drug.side_effects ?? [],
        warnings: drug.warnings ?? [],
        food_interactions: drug.food_interactions ?? [],
        storage: String(drug.storage ?? ''),
        missed_dose: String(drug.missed_dose ?? ''),
        pill_image_path: String(drug.pill_image_path ?? ''),
        pill_color: String(drug.pill_color ?? ''),
        pill_shape: String(drug.pill_shape ?? ''),
        pill_imprint: String(drug.pill_imprint ?? ''),
      };
    }

    // Save drug cache to localStorage.
    try {
      localStorage.setItem(LS_KEY_DRUG_CACHE, JSON.stringify(Object.entries(drugCache)));
      console.log(`[AfterCare] Cached ${Object.keys(drugCache).length} drugs in localStorage`);
    } catch (lsErr) {
      console.warn('[AfterCare] localStorage drug cache failed (storage full?):', lsErr);
    }

    // SECONDARY: Also seed into RvLite if available (for vector search).
    if (db) {
      const zeroVector = new Float32Array(VECTOR_DIMENSIONS);
      let rvliteCount = 0;
      for (const [id, metadata] of Object.entries(drugCache)) {
        try {
          db.insert_with_id(id, zeroVector, metadata);
          rvliteCount++;
        } catch {
          // Duplicate ID on re-seed — skip silently.
        }
      }
      console.log(`[AfterCare] Seeded ${rvliteCount} drugs into RvLite`);
    }

    // Seed drug interactions into localStorage.
    try {
      const intResp = await fetch('/interactions.json');
      if (intResp.ok) {
        const interactions: Record<string, unknown>[] = await intResp.json();
        lsSet(LS_KEY_DRUG_INTERACTIONS, interactions);
        console.log(`[AfterCare] Seeded ${interactions.length} drug interactions`);
      }
    } catch (intErr) {
      console.warn('[AfterCare] Could not seed interactions:', intErr);
    }

    // Persist RvLite state to IndexedDB.
    if (db) {
      try { await db.save(); } catch { /* IndexedDB may fail, that's OK */ }
    }
    localStorage.setItem(LS_KEY_SEEDED, 'true');
  } catch (err) {
    console.error('[AfterCare] Drug data seeding failed:', err);
  }
}

async function seedDemoPatient(): Promise<void> {
  // Always ensure patient name is set (even if meds already seeded)
  if (!localStorage.getItem(LS_KEY_PATIENT_NAME)) {
    localStorage.setItem(LS_KEY_PATIENT_NAME, 'Manisha');
  }

  const existing = lsGet<unknown[]>(LS_KEY_PATIENT_MEDS, []);
  if (existing.length > 0) return;

  console.log('[AfterCare] Seeding demo patient with 4 medications...');

  const demoMeds = [
    {
      id: 'demo-met-500',
      drug_rxcui: '6809',
      drug_name: 'Metformin 500mg',
      strength: '500mg',
      frequency: 'twice_daily',
      times: ['08:00', '20:00'],
      instructions: 'Take with food. Do not crush or chew extended-release tablets.',
      active: 1,
    },
    {
      id: 'demo-lis-10',
      drug_rxcui: '29046',
      drug_name: 'Lisinopril 10mg',
      strength: '10mg',
      frequency: 'once_daily',
      times: ['08:00'],
      instructions: 'Take in the morning. Monitor blood pressure regularly.',
      active: 1,
    },
    {
      id: 'demo-ator-20',
      drug_rxcui: '83367',
      drug_name: 'Atorvastatin 20mg',
      strength: '20mg',
      frequency: 'once_daily',
      times: ['22:00'],
      instructions: 'Take at bedtime. Avoid grapefruit juice.',
      active: 1,
    },
    {
      id: 'demo-omep-20',
      drug_rxcui: '7646',
      drug_name: 'Omeprazole 20mg',
      strength: '20mg',
      frequency: 'once_daily',
      times: ['07:30'],
      instructions: 'Take 30 minutes before breakfast on an empty stomach.',
      active: 1,
    },
  ];

  lsSet(LS_KEY_PATIENT_MEDS, demoMeds);

  // Set patient name (always update to ensure correct name)
  localStorage.setItem(LS_KEY_PATIENT_NAME, 'Manisha');

  console.log('[AfterCare] Demo patient seeded with 4 medications');

  // Seed EMR data alongside patient data
  seedDemoEmr();
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Initialize the app database layer.
 * Strategy: localStorage is ALWAYS the primary store (100% reliable).
 * RvLite WASM and RVF WASM are optional enhancements for vector search.
 * The app MUST work even if all WASM fails.
 */
async function initDb(): Promise<RvLite | null> {
  let db: RvLite | null = null;

  // Try to initialize WASM engines (both are optional).
  try {
    const results = await Promise.allSettled([
      initRvLite(),
      initRvfStore(),
    ]);

    if (results[0].status === 'rejected') {
      console.warn('[AfterCare] RvLite WASM init failed (will use localStorage only):', results[0].reason);
    } else {
      // Try to create/restore the RvLite vector database.
      try {
        const config = new RvLiteConfig(VECTOR_DIMENSIONS)
          .with_distance_metric(DISTANCE_METRIC);

        const hasSaved = await RvLite.has_saved_state();
        if (hasSaved) {
          db = await RvLite.load(config);
          console.log(`[AfterCare] Restored RvLite from IndexedDB (${db!.len()} vectors)`);
        } else {
          db = new RvLite(config);
          await db.init_storage();
          console.log('[AfterCare] Created fresh RvLite database');
        }
      } catch (dbErr) {
        console.warn('[AfterCare] RvLite database creation failed:', dbErr);
        db = null;
      }
    }

    if (results[1].status === 'rejected') {
      console.warn('[AfterCare] RVF WASM init failed (vector search unavailable):', results[1].reason);
    }
  } catch (err) {
    console.warn('[AfterCare] WASM initialization failed entirely:', err);
  }

  // Seed drug data (works with or without RvLite — localStorage is primary).
  await seedDrugData(db);

  // Seed demo patient into localStorage.
  await seedDemoPatient();

  const rvliteStatus = db ? `${db.len()} vectors` : 'unavailable';
  const rvfStatus = isRvfReady() ? 'ready' : 'unavailable';
  console.log(`[AfterCare] Database initialized. RvLite: ${rvliteStatus}, RVF: ${rvfStatus}`);

  dbInstance = db;
  return db;
}

// ---------------------------------------------------------------------------
// Public API — Drug queries via RvLite native API
// ---------------------------------------------------------------------------

export async function getDb(): Promise<RvLite | null> {
  if (dbInstance !== null) return dbInstance;
  if (initPromise) return initPromise;

  initPromise = initDb().catch((err) => {
    console.warn('[AfterCare] Database init failed, using localStorage only:', err);
    initPromise = null;
    return null;
  });
  return initPromise;
}

/**
 * Compatibility shim: wraps RvLite as a SQL-like interface.
 */
export async function getCypher(): Promise<RvLite | null> {
  return getDb();
}

// ---------------------------------------------------------------------------
// Public API — Drug metadata from localStorage cache (always works)
// ---------------------------------------------------------------------------

/**
 * Get a drug by ID from the localStorage cache.
 * This is the PRIMARY lookup method — works even if WASM is unavailable.
 */
export function getDrugFromCache(drugId: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(LS_KEY_DRUG_CACHE);
    if (!raw) return null;
    const entries: [string, Record<string, unknown>][] = JSON.parse(raw);
    const match = entries.find(([id]) => id === drugId);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Search drugs by name in localStorage cache.
 */
export function searchDrugsFromCache(query: string): { id: string; meta: Record<string, unknown> }[] {
  try {
    const raw = localStorage.getItem(LS_KEY_DRUG_CACHE);
    if (!raw) return [];
    const entries: [string, Record<string, unknown>][] = JSON.parse(raw);
    const lower = query.toLowerCase();
    return entries
      .filter(([, meta]) => {
        const name = String(meta.generic_name ?? '').toLowerCase();
        const brands = JSON.stringify(meta.brand_names ?? []).toLowerCase();
        return name.includes(lower) || brands.includes(lower);
      })
      .map(([id, meta]) => ({ id, meta }));
  } catch {
    return [];
  }
}

/**
 * Get all drugs from localStorage cache.
 */
export function getAllDrugsFromCache(): { id: string; meta: Record<string, unknown> }[] {
  try {
    const raw = localStorage.getItem(LS_KEY_DRUG_CACHE);
    if (!raw) return [];
    const entries: [string, Record<string, unknown>][] = JSON.parse(raw);
    return entries.map(([id, meta]) => ({ id, meta }));
  } catch {
    return [];
  }
}

/**
 * Perform HNSW vector search using the RVF cognitive container.
 * Falls back to RvLite's built-in search.
 */
export async function searchVectors(
  queryVector: Float32Array,
  k: number,
): Promise<{ id: string; distance: number }[]> {
  if (isRvfReady()) {
    const results = queryRvf(queryVector, k);
    return results.map((r) => ({ id: String(r.id), distance: r.distance }));
  }

  const db = await getDb();
  if (!db) return [];

  try {
    const results = db.search(queryVector, k);
    return results || [];
  } catch {
    return [];
  }
}

export async function save(): Promise<void> {
  const db = await getDb();
  if (db) await db.save();
}

// ---------------------------------------------------------------------------
// Public API — Patient medications (localStorage-backed)
// ---------------------------------------------------------------------------

export interface PatientMedRecord {
  id: string;
  drug_rxcui: string;
  drug_name: string;
  strength: string;
  frequency: string;
  times: string[];
  instructions: string;
  active: number;
}

export function getPatientName(): string {
  return localStorage.getItem(LS_KEY_PATIENT_NAME) || '';
}

export function setPatientName(name: string): void {
  localStorage.setItem(LS_KEY_PATIENT_NAME, name);
}

export function getPatientMedications(): PatientMedRecord[] {
  return lsGet<PatientMedRecord[]>(LS_KEY_PATIENT_MEDS, [])
    .filter((m) => m.active === 1);
}

export function getAllPatientMedications(): PatientMedRecord[] {
  return lsGet<PatientMedRecord[]>(LS_KEY_PATIENT_MEDS, []);
}

export function addPatientMedication(med: PatientMedRecord): void {
  const meds = lsGet<PatientMedRecord[]>(LS_KEY_PATIENT_MEDS, []);
  meds.push(med);
  lsSet(LS_KEY_PATIENT_MEDS, meds);
}

// ---------------------------------------------------------------------------
// Public API — Dose log (localStorage-backed)
// ---------------------------------------------------------------------------

export interface DoseLogRecord {
  id: string;
  med_id: string;
  action: string;
  scheduled_time: string;
  actual_time: string;
}

export function getDoseLogs(): DoseLogRecord[] {
  return lsGet<DoseLogRecord[]>(LS_KEY_DOSE_LOG, []);
}

export function getTodayDoseLogs(): DoseLogRecord[] {
  const todayStr = new Date().toISOString().slice(0, 10);
  return getDoseLogs().filter((l) => l.actual_time.startsWith(todayStr));
}

export function addDoseLog(log: DoseLogRecord): void {
  const logs = lsGet<DoseLogRecord[]>(LS_KEY_DOSE_LOG, []);
  logs.push(log);
  lsSet(LS_KEY_DOSE_LOG, logs);
}

/**
 * Log a dose action (taken/skipped) for a medication.
 * Convenience wrapper that creates the DoseLogRecord automatically.
 */
export function logDose(medId: string, action: 'taken' | 'skipped' | 'missed', scheduledTime?: string): void {
  const now = new Date();
  const nowISO = now.toISOString();
  // Use provided scheduled time (e.g. "08:00") or derive short time from now
  const schedTime = scheduledTime || `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  addDoseLog({
    id: `dose-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    med_id: medId,
    action,
    scheduled_time: schedTime,
    actual_time: nowISO,
  });
}

/**
 * Undo the last "taken" dose for a medication today.
 * Used by home screen "Undo" button. Avoids raw localStorage access.
 */
export function undoLastDose(medId: string): void {
  const logs = lsGet<DoseLogRecord[]>(LS_KEY_DOSE_LOG, []);
  const todayStr = new Date().toISOString().slice(0, 10);
  // Find last taken log for this med today (search from end)
  for (let i = logs.length - 1; i >= 0; i--) {
    const l = logs[i];
    if (l.med_id === medId && l.action === 'taken' && l.actual_time.startsWith(todayStr)) {
      logs.splice(i, 1);
      lsSet(LS_KEY_DOSE_LOG, logs);
      return;
    }
  }
}

// ---------------------------------------------------------------------------
// Public API — Drug interactions (localStorage-backed)
// ---------------------------------------------------------------------------

export interface InteractionRecord {
  id: string;
  drug_a_id: string;
  drug_b_id: string;
  severity: string;
  description: string;
  clinical_effect: string;
}

export function getDrugInteractions(): InteractionRecord[] {
  return lsGet<InteractionRecord[]>(LS_KEY_DRUG_INTERACTIONS, []);
}

export function findInteraction(drugAId: string, drugBId: string): InteractionRecord | null {
  const interactions = getDrugInteractions();
  return interactions.find(
    (ix) =>
      (ix.drug_a_id === drugAId && ix.drug_b_id === drugBId) ||
      (ix.drug_a_id === drugBId && ix.drug_b_id === drugAId),
  ) ?? null;
}
